/**
 * MUTAGIM.SHOP — Netflix Home Page (Phase 6 + Phase 18)
 * Page d'accueil style Netflix avec lignes de produits dynamiques
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { HeroSlider } from '../components/HeroSlider';
import { NetflixRow } from '../components/product/NetflixRow';
import { FlashSaleRow } from '../components/FlashSaleRow';
import { BundleRow } from '../components/BundleRow';
import { ScrollToTopButton } from '../components/ScrollToTopButton';
import { CompareFloatingButton } from '../components/CompareFloatingButton';
import { useCrewTracking } from '../hooks/useCrewTracking';
import { BrandsMarquee } from '../components/BrandsMarquee';
import { PromoMarquee } from '../components/PromoMarquee';
import { HomepageCategories } from '../components/HomepageCategories';
import { ReviewsMarquee } from '../components/ReviewsMarquee';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function HomePage({ isPreviewMode = false }) {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track crew referral links (?ref=CODE)
  useCrewTracking();
  
  const previewToken = searchParams.get('token');
  const inPreview = isPreviewMode || !!previewToken;
  const lang = i18n.language?.split('-')[0] || 'fr';

  // Charger les lignes Netflix et les catégories
  const loadHomeData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Charger en parallèle les rows, les catégories, et TOUS les produits
      const [rowsResponse, categoriesResponse, productsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/storefront/home-rows`, { params: { lang } }),
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/products`, { params: { limit: 100 } })
      ]);
      
      const fetchedRows = rowsResponse.data.rows || [];
      const allProducts = productsResponse.data.products || [];
      
      // Créer la row "Dernières Arrivées" à partir des produits les plus récents
      const sortedByDate = [...allProducts].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      const newArrivalsRow = {
        id: 'nouveautes',
        type: 'new',
        title: { fr: '🆕 Dernières Arrivées', en: '🆕 New Arrivals', he: '🆕 חדשים' },
        emoji: '🆕',
        products: sortedByDate.slice(0, 15)
      };
      
      // Ajouter la row nouveautés en premier si elle n'existe pas déjà
      const hasNewRow = fetchedRows.some(r => r.type === 'new' || r.type === 'nouveautes');
      if (!hasNewRow && newArrivalsRow.products.length > 0) {
        fetchedRows.unshift(newArrivalsRow);
      }
      
      setRows(fetchedRows);
      const cats = categoriesResponse.data.categories || [];
      setCategories(cats);
      
      // Charger les produits seulement pour les catégories avec des produits (products_count > 0)
      // et limiter à 10 catégories par type pour éviter le rate-limiting
      const productCats = cats.filter(c => (c.type === 'product' || !c.type) && (c.products_count || 0) > 0).slice(0, 10);
      const brandCats = cats.filter(c => c.type === 'brand' && (c.products_count || 0) > 0).slice(0, 15);
      const catsToLoad = [...productCats, ...brandCats];
      
      const categoryProductsMap = {};
      
      // Charger par lots de 5 pour éviter le rate-limiting
      const batchSize = 5;
      for (let i = 0; i < catsToLoad.length; i += batchSize) {
        const batch = catsToLoad.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (cat) => {
            try {
              const res = await axios.get(`${API_URL}/api/products`, {
                params: { category: cat.id, limit: 20 }
              });
              categoryProductsMap[cat.slug] = res.data.products || [];
            } catch (err) {
              console.error(`Error loading products for ${cat.slug}:`, err);
              categoryProductsMap[cat.slug] = [];
            }
          })
        );
      }
      setCategoryProducts(categoryProductsMap);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching home data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    loadHomeData();
  }, [loadHomeData]);

  // Filtrer les lignes par type
  const newProductsRow = rows.find(r => r.type === 'new' || r.type === 'nouveautes' || r.id === 'new' || r.id === 'nouveautes');
  const popularRow = rows.find(r => r.type === 'popular' || r.type === 'populaires' || r.id === 'popular' || r.id === 'populaires');
  
  // Séparer les catégories produits et marques depuis les ROWS (pas les catégories séparées)
  // Les rows contiennent déjà les produits chargés
  const categoryRows = rows.filter(r => r.type === 'category' && r.products?.length > 0);
  const brandRows = rows.filter(r => r.type === 'brand' && r.products?.length > 0);

  // Fallback: si pas de category/brand rows, utiliser les catégories chargées séparément
  const productCategories = categoryRows.length > 0 ? [] : categories
    .filter(c => (c.type === 'product' || !c.type) && (c.products_count || 0) > 0)
    .slice(0, 10);
  const brandCategories = categoryRows.length > 0 ? [] : categories
    .filter(c => c.type === 'brand' && (c.products_count || 0) > 0)
    .slice(0, 15);

  // Composant pour afficher un bandeau de catégorie (même style que Nouveautés/Populaires)
  const CategoryRow = ({ category, products }) => {
    if (!products || products.length === 0) return null;
    
    const categoryName = category.name?.[lang] || category.name?.fr || category.slug;
    
    return (
      <NetflixRow 
        row={{
          id: `cat-${category.slug}`,
          title: { [lang]: categoryName },
          emoji: category.emoji || '🏷️',
          products: products,
          linkTo: `/category/${category.slug}`
        }} 
      />
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-black overflow-x-hidden" data-testid="home-page">
      <Header isPreviewMode={inPreview} />
      
      <main className="flex-1 overflow-x-hidden">
        {/* 1. Hero Slider */}
        <HeroSlider />
        
        {/* 2. Bandeau Marques Partenaires (tout en haut, sous le slider) */}
        <BrandsMarquee />
        
        {/* ========== ZONE PRODUITS ========== */}
        <div className="max-w-[1920px] mx-auto py-8 space-y-10 md:space-y-12 px-4 md:px-8 lg:px-12">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-mutagim-red" />
              <p className="text-gray-400 text-sm">{t('common.loading', 'Chargement...')}</p>
            </div>
          ) : (
            <>
              {/* 1. 🆕 DERNIÈRES ARRIVÉES (toujours en premier) */}
              {newProductsRow && (
                <NetflixRow key={newProductsRow.id} row={newProductsRow} />
              )}
              
              {/* 2. 🔥 LES PLUS POPULAIRES */}
              {popularRow && (
                <NetflixRow key={popularRow.id} row={popularRow} />
              )}
              
              {/* 3. Ventes Flash (si actives) */}
              <FlashSaleRow />
              
              {/* 4. 📦 CATÉGORIES PAR TYPE DE PRODUIT (depuis API rows) */}
              {categoryRows.map((row) => (
                <NetflixRow key={row.id} row={row} />
              ))}
              
              {/* Fallback: si pas de category rows, utiliser les catégories chargées séparément */}
              {productCategories.map((category) => (
                <CategoryRow 
                  key={category.slug}
                  category={category}
                  products={categoryProducts[category.slug] || []}
                />
              ))}
              
              {/* 5. 🏷️ CATÉGORIES PAR MARQUE (depuis API rows) */}
              {brandRows.map((row) => (
                <NetflixRow key={row.id} row={row} />
              ))}
              
              {/* Fallback: si pas de brand rows, utiliser les catégories chargées séparément */}
              {brandCategories.map((category) => (
                <CategoryRow 
                  key={category.slug}
                  category={category}
                  products={categoryProducts[category.slug] || []}
                />
              ))}
              
              {/* 6. 🎁 Nos Packs */}
              <BundleRow />
            </>
          )}
        </div>
        
        {/* ========== ZONE SOCIAL PROOF (Bas de page) ========== */}
        
        {/* 8. Bandeau Avis Clients */}
        <ReviewsMarquee />
        
        {/* 9. Cartes Catégories (tout en bas) */}
        <HomepageCategories />
        
      </main>
      
      <Footer />
      <ScrollToTopButton />
      <CompareFloatingButton />
    </div>
  );
}

export default HomePage;
